import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Col,
  Row,
  Skeleton,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  MenuOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import ProposalService from '../../services/ProposalService';
import SettingService from '../../services/SettingService';
import AttributeService from '../../services/AttributeService';
import {
  formatPhoneNumberDisplay,
  formatToTRY,
} from '../../utils';
import kontrolXLogo from '../../assets/kontrolx.png';
import { getContent, resolveProposalDocumentVersion } from '../../components/Proposal/generatePdf';
import { buildProposalPricing, ProposalPricing } from '../../components/Proposal/proposalPricing';
import './ProposalPreview.scss';

type SectionId = 'cover' | 'value' | 'process' | 'price' | 'legal';

type LegalSection = {
  id: string;
  title: string;
  html: string;
};

type ProcessDurations = {
  controlDays: number;
  reportDays: number;
};

const SECTION_ITEMS: Array<{ id: SectionId; label: string; icon: React.ReactNode }> = [
  { id: 'cover', label: 'Kapak', icon: <FileTextOutlined /> },
  { id: 'value', label: 'Değer Önerisi', icon: <SafetyCertificateOutlined /> },
  { id: 'process', label: 'Süreç Planı', icon: <ClockCircleOutlined /> },
  { id: 'price', label: 'Hizmet Bedeli', icon: <span className="proposal-preview-tl-icon">₺</span> },
  { id: 'legal', label: 'Yasal Şartlar', icon: <ScheduleOutlined /> },
];

const numberValue = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatTl = (value: number): string => `${formatToTRY(value, false)} TL`;

const ProposalPreview: React.FC = () => {
  const { proposalId } = useParams();
  const [proposal, setProposal] = useState<any | null>(null);
  const [allAttributes, setAllAttributes] = useState<any[]>([]);
  const [serviceFee, setServiceFee] = useState<number>(2950);
  const [proposalVersionSetting, setProposalVersionSetting] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>('cover');
  const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth <= 1200);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const sectionRefs = useRef<Record<SectionId, HTMLElement | null>>({
    cover: null,
    value: null,
    process: null,
    price: null,
    legal: null,
  });

  useEffect(() => {
    const numericProposalId = Number(proposalId);
    if (!Number.isFinite(numericProposalId) || numericProposalId <= 0) {
      setError('Geçerli teklif bulunamadı.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [proposalResponse, settings, attributesResponse] = await Promise.all([
          ProposalService.get(numericProposalId),
          SettingService.listSettings().catch(() => []),
          AttributeService.getAll().catch(() => ({ data: [] })),
        ]);

        if (cancelled) {
          return;
        }

        const proposalData = proposalResponse?.data ?? null;
        const feeSetting = Array.isArray(settings)
          ? settings.find((item: any) => item?.key === 'SERVICE_FEE')
          : null;
        const proposalVersionConfig = Array.isArray(settings)
          ? settings.find((item: any) => item?.key === 'PROPOSAL_VERSION')
          : null;
        const feeRaw = feeSetting?.effectiveValue ?? feeSetting?.value ?? feeSetting?.defaultValue;
        const proposalVersionRaw =
          proposalVersionConfig?.effectiveValue ??
          proposalVersionConfig?.value ??
          proposalVersionConfig?.defaultValue ??
          '';
        const feeParsed = Number(feeRaw);
        const rawAttributes = Array.isArray(attributesResponse?.data) ? attributesResponse.data : [];
        const sortedAttributes = [...rawAttributes].sort((a: any, b: any) => numberValue(a?.order) - numberValue(b?.order));

        setProposal(proposalData);
        setAllAttributes(sortedAttributes);
        setServiceFee(Number.isFinite(feeParsed) ? feeParsed : 2950);
        setProposalVersionSetting(proposalVersionRaw);
      } catch (fetchError) {
        if (!cancelled) {
          setError('Teklif verisi yüklenemedi.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  useEffect(() => {
    const onResize = () => {
      const nextIsMobile = window.innerWidth <= 1200;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = entry.target.getAttribute('data-section') as SectionId | null;
            if (section) {
              setActiveSection(section);
            }
          }
        });
      },
      {
        rootMargin: '-25% 0px -55% 0px',
        threshold: 0,
      },
    );

    SECTION_ITEMS.forEach((section) => {
      const element = sectionRefs.current[section.id];
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [loading, proposal]);

  const pricing = useMemo<ProposalPricing>(() => buildProposalPricing(proposal, allAttributes), [proposal, allAttributes]);

  const createdAtText = useMemo(() => {
    const date = proposal?.createdAt ? new Date(proposal.createdAt) : new Date();
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }, [proposal]);

  const proposalNumberText = useMemo(() => {
    const proposalNumber = proposal?.proposalNumber || '-';
    const accountMarker = proposal?.updatedAccountId != null ? `${proposal.updatedAccountId}` : '';
    return `${proposalNumber}${accountMarker ? `#${accountMarker}#` : ''}`;
  }, [proposal]);

  const addressText = useMemo(() => {
    const selectedAddress =
      proposal?.address ||
      proposal?.company?.addresses?.find((address: any) => address?.type === 'COMPANY') ||
      proposal?.company?.addresses?.find((address: any) => address?.type === 'BILLING') ||
      proposal?.company?.addresses?.[0];

    if (!selectedAddress) {
      return '-';
    }

    const line = [selectedAddress?.addressLine1, selectedAddress?.state, selectedAddress?.city]
      .filter((item) => Boolean(item))
      .map((item) => String(item).replace(/\n/g, ' ').trim())
      .join(' - ');

    return line || '-';
  }, [proposal]);

  const legalSections = useMemo<LegalSection[]>(() => {
    if (!proposal) {
      return [];
    }

    const selectedAddress =
      proposal?.address ||
      proposal?.company?.addresses?.find((address: any) => address?.type === 'COMPANY') ||
      proposal?.company?.addresses?.find((address: any) => address?.type === 'BILLING') ||
      proposal?.company?.addresses?.[0];

    const addressRef = {
      current: {
        addressLine1: selectedAddress?.addressLine1 || '-',
        state: selectedAddress?.state || '-',
        city: selectedAddress?.city || '-',
      },
    };

    const htmlContent = getContent(proposal, addressRef as any, serviceFee);
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const headings = Array.from(doc.querySelectorAll('h1'));
    const sections: LegalSection[] = [];

    headings.forEach((heading, index) => {
      const titleText = (heading.textContent || '').trim();
      if (!/^[1-4]\./.test(titleText)) {
        return;
      }

      const nextHeading = headings[index + 1] || null;
      const wrapper = doc.createElement('div');

      let sibling = heading.nextElementSibling;
      while (sibling && sibling !== nextHeading) {
        wrapper.appendChild(sibling.cloneNode(true));
        sibling = sibling.nextElementSibling;
      }

      sections.push({
        id: `legal-${index + 1}`,
        title: titleText,
        html: wrapper.innerHTML,
      });
    });

    return sections;
  }, [proposal, serviceFee]);

  const validityText = useMemo(() => {
    return proposal?.validityDay ? `${proposal.validityDay} Gün` : '30 Gün';
  }, [proposal]);

  const proposalDocumentVersion = useMemo(
    () => resolveProposalDocumentVersion(proposal, proposalVersionSetting),
    [proposal, proposalVersionSetting],
  );

  const processDurations = useMemo<ProcessDurations>(() => {
    const attributes = Array.isArray(proposal?.attributes) ? proposal.attributes : [];
    if (attributes.length === 0) {
      return { controlDays: 0, reportDays: 0 };
    }

    let totalReportMinutes = 0;
    const groupedControlMinutes: Record<string, number> = {};

    attributes.forEach((attribute: any) => {
      const quantity = numberValue(attribute?.quantity);
      const period = numberValue(attribute?.period);
      const fieldControlTime = numberValue(attribute?.fieldControlTime);
      const reportPreparationTime = numberValue(attribute?.reportPreparationTime);
      const offeringName = String(attribute?.offeringName || 'GENEL');

      totalReportMinutes += period * quantity * reportPreparationTime;
      groupedControlMinutes[offeringName] =
        (groupedControlMinutes[offeringName] || 0) + period * quantity * fieldControlTime;
    });

    const controlDays = Object.values(groupedControlMinutes).reduce((sum, minutes) => {
      return sum + Math.ceil(minutes / 480);
    }, 0);

    const reportDays = Number((totalReportMinutes / 480).toFixed(1));

    return {
      controlDays,
      reportDays,
    };
  }, [proposal]);

  const onSectionClick = (sectionId: SectionId) => {
    const target = sectionRefs.current[sectionId];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="proposal-preview-loading">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" showIcon />;
  }

  if (!proposal) {
    return <Empty description="Teklif verisi bulunamadı" />;
  }

  const companyName = String(proposal?.company?.name || '-').toLocaleUpperCase('tr-TR');
  const controlDaysText = processDurations.controlDays > 0 ? `${processDurations.controlDays} İŞ GÜNÜ` : '-';
  const reportDaysText = processDurations.reportDays > 0 ? `${processDurations.reportDays} İŞ GÜNÜ` : '-';

  return (
    <div className="proposal-preview-page">
      {isMobile && (
        <div className="proposal-preview-mobile-header">
          <img
            src={kontrolXLogo}
            alt="Kontrol X"
          />
          <button
            type="button"
            className="proposal-preview-hamburger"
            onClick={() => setIsSidebarOpen((prev) => !prev)}
            aria-label="Menüyü aç"
          >
            {isSidebarOpen ? <CloseOutlined /> : <MenuOutlined />}
          </button>
        </div>
      )}

      {isMobile && isSidebarOpen && (
        <button
          type="button"
          className="proposal-preview-overlay"
          aria-label="Menüyü kapat"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`proposal-preview-sidebar ${isMobile ? 'mobile' : ''} ${isSidebarOpen ? 'mobile-open' : ''}`}>
        <div className="proposal-preview-brand">
          <img
            src={kontrolXLogo}
            alt="Kontrol X"
          />
        </div>
        <nav className="proposal-preview-nav">
          {SECTION_ITEMS.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <button
                type="button"
                key={section.id}
                className={`proposal-preview-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => onSectionClick(section.id)}
              >
                <span className="proposal-preview-nav-icon">{section.icon}</span>
                <span>{section.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="proposal-preview-sidebar-footer">© {new Date().getFullYear()} Kontrol X</div>
      </aside>

      <main className="proposal-preview-content">
        <section ref={(node) => { sectionRefs.current.cover = node; }} data-section="cover" className="proposal-preview-section cover-section">
          <div className="cover-card">
            <div className="cover-badge">Periyodik Kontrol Hizmeti Fiyat Teklifi</div>
            <h1>İşletmenizi Güvenli Geleceğe Hazırlıyoruz</h1>
            <div className="cover-divider" />

            <div className="cover-company-title">{proposalDocumentVersion || '-'}</div>
            <div className="cover-company-name">{companyName}</div>
            <div className="cover-company-address">{addressText.toLocaleUpperCase('tr-TR')}</div>

            <div className="cover-price-box">
              <div className="cover-price-title">TOPLAM KONTROL MUAYENE ÜCRETİ</div>
              <div className="cover-price-value">{formatTl(pricing.subtotalExcludingKdv)}</div>
            </div>

            <div className="cover-meta">
              <div><strong>Teklif No:</strong> {proposalNumberText}</div>
              <div><strong>Tarih:</strong> {createdAtText}</div>
              <div><strong>Geçerlilik:</strong> {validityText}</div>
            </div>

            <div className="cover-footer-company">
              <img
                src={kontrolXLogo}
                alt="Kontrol X"
              />
              <div>Normcert Uygunluk Değerlendirme Hizmetleri Ltd. Şti.</div>
              <div>İkitelli OSB Giykoop 2A Blok No:103 Başakşehir - İstanbul</div>
              <div>Tel: 0212 407 04 05 - info@kontrolx.com.tr</div>
            </div>
          </div>
        </section>

        <section ref={(node) => { sectionRefs.current.value = node; }} data-section="value" className="proposal-preview-section value-section">
          <h2>Sizin İçin Neler Değişecek?</h2>
          <p className="section-subtitle">
            "Denetim korkusundan, teknik emniyet konforuna geçin. Kontrol X ile sadece bir 'onay' değil,
            tesisinizin mühendislik check-up’ını yaptırıyorsunuz."
          </p>

          <Row gutter={[20, 20]} className="value-grid">
            <Col xs={24} lg={12}>
              <article className="value-card">
                <ClockCircleOutlined />
                <h3>Rakipsiz Hız</h3>
                <p>Muayene tamamlandığı andan itibaren sadece 1 gün içinde yasal geçerliliği olan raporunuz dijital olarak teslim edilir.</p>
                <strong>24 SAATTE TESLİMAT</strong>
              </article>
            </Col>
            <Col xs={24} lg={12}>
              <article className="value-card">
                <SafetyCertificateOutlined />
                <h3>TÜRKAK Güvencesi</h3>
                <p>Tüm süreçlerimiz akredite mühendisler tarafından yönetilir, bakanlık denetimlerinde %100 uyumluluk sağlar.</p>
                <strong>TAM YASAL UYUMLULUK</strong>
              </article>
            </Col>
            <Col xs={24} lg={12}>
              <article className="value-card">
                <FileTextOutlined />
                <h3>Dijital Arşiv</h3>
                <p>Raporlarınız asla kaybolmaz. Online müşteri portalımız üzerinden dilediğiniz an tüm geçmiş kayıtlara erişebilirsiniz.</p>
                <strong>7/24 PANEL ERİŞİMİ</strong>
              </article>
            </Col>
            <Col xs={24} lg={12}>
              <article className="value-card">
                <CheckCircleOutlined />
                <h3>Maliyet Optimizasyonu</h3>
                <p>Periyodik kontrollerimiz ekipman ömrünü uzatır, plansız duruşları ve ağır iş kazası risklerini minimize eder.</p>
                <strong>RİSK YÖNETİMİ</strong>
              </article>
            </Col>
          </Row>
        </section>

        <section ref={(node) => { sectionRefs.current.process = node; }} data-section="process" className="proposal-preview-section process-section">
          <h2>Operasyonel Akış Şeması</h2>
          <div className="process-timeline">
            <div className="process-step left">
              <span className="step-badge">24 SAAT İÇİNDE</span>
              <h3>Onay & Planlama</h3>
              <p>Teklifiniz onayladığınız andan itibaren 24 saat içinde saha randevunuz oluşturulur.</p>
            </div>
            <div className="process-point">1</div>

            <div className="process-point">2</div>
            <div className="process-step right">
              <span className="step-badge">{controlDaysText}</span>
              <h3>Saha Muayenesi</h3>
              <p>Uzman mühendislerimiz sahada detaylı teknik inceleme ve testleri gerçekleştirir.</p>
            </div>

            <div className="process-step left highlighted">
              <span className="step-badge">{reportDaysText}</span>
              <h3>Analiz & Raporlama</h3>
              <p>Denetim verileri işlenir ve güvenli e-imza ile sadece 24 saatte raporunuz teslim edilir.</p>
            </div>
            <div className="process-point">3</div>
          </div>
        </section>

        <section ref={(node) => { sectionRefs.current.price = node; }} data-section="price" className="proposal-preview-section price-section">
          <h2>Kontrol Muayene Hizmet Bedeli</h2>
          <p className="pricing-note">5.1. Periyodik kontrolü gerçekleştirilecek iş ekipmanları ve hizmet bedelleri aşağıdaki tabloda sunulmuştur.</p>
          <p className="pricing-note">5.2. Aşağıda belirtilen hizmet bedellerine masraflar dahil değildir.</p>

          <div className="price-table-wrapper">
            <table className="price-table">
              <thead>
                <tr>
                  <th>İŞ EKİPMANI</th>
                  <th>AZAMİ SÜRE</th>
                  <th>KONTROL SAYISI</th>
                  <th>PERİYOT</th>
                  <th>MİKTAR</th>
                  <th>BİRİM FİYAT</th>
                  <th>TOPLAM FİYAT</th>
                </tr>
              </thead>
              <tbody>
                {pricing.tables.equipmentUngrouped.length === 0 && pricing.tables.equipmentGrouped.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="no-row">Kalem bilgisi bulunamadı.</td>
                  </tr>
                ) : (
                  <>
                    {pricing.tables.equipmentUngrouped.map((row) => (
                      <tr key={row.key}>
                        <td>{row.name}</td>
                        <td>{row.maxDurationText}</td>
                        <td>{row.controlCount}</td>
                        <td>{row.periodText}</td>
                        <td className="quantity">{row.quantityWithUnit}</td>
                        <td className="amount">{formatTl(row.unitPrice)}</td>
                        <td className="amount">{formatTl(row.totalPrice)}</td>
                      </tr>
                    ))}
                    {pricing.tables.equipmentGrouped.map((row) => (
                      <tr key={row.key}>
                        <td>{row.toolName}</td>
                        <td>{row.maxDurationText}</td>
                        <td>{row.controlCount}</td>
                        <td>{row.periodText}</td>
                        <td className="quantity">{row.measurementDetails}</td>
                        <td className="amount">-</td>
                        <td className="amount">{formatTl(row.totalPrice)}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>TOPLAM KONTROL MUAYENE ÜCRETİ</td>
                  <td className="amount total">{formatTl(pricing.subtotalExcludingKdv)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {(pricing.tables.measurementUngrouped.length > 0 || pricing.tables.measurementComposite.length > 0) && (
            <div className="price-table-wrapper measurement-table">
              <table className="price-table">
                <thead>
                  <tr>
                    <th>ÖLÇÜM TÜRÜ</th>
                    <th>AZAMİ SÜRE</th>
                    <th>KONTROL SAYISI</th>
                    <th>PERİYOT</th>
                    <th>MİKTAR</th>
                    <th>BİRİM FİYAT</th>
                    <th>TOPLAM FİYAT</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.tables.measurementUngrouped.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.maxDurationText}</td>
                      <td>{row.controlCount}</td>
                      <td>{row.periodText}</td>
                      <td className="quantity">{row.quantityWithUnit}</td>
                      <td className="amount">{formatTl(row.unitPrice)}</td>
                      <td className="amount">{formatTl(row.totalPrice)}</td>
                    </tr>
                  ))}
                  {pricing.tables.measurementComposite.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.maxDurationText}</td>
                      <td>{row.controlCount}</td>
                      <td>{row.periodText}</td>
                      <td className="quantity">{row.detail}</td>
                      <td className="amount">-</td>
                      <td className="amount">{formatTl(row.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="price-breakdown-grid">
            {pricing.equipmentSummaryRows.length > 0 && (
              <div className="price-table-wrapper measurement-table">
                <table className="price-table price-summary-table">
                  <tbody>
                    {pricing.equipmentSummaryRows.map((row, index) => (
                      <tr key={`eq-summary-${index}`} className={row.isGrand ? 'summary-grand' : ''}>
                        <td>{row.isDiscount ? <><span>İndirim </span><span className="discount-mark">(-)</span></> : row.label}</td>
                        <td className="amount">{formatTl(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pricing.measurementSummaryRows.length > 0 && (
              <div className="price-table-wrapper measurement-table">
                <table className="price-table price-summary-table">
                  <tbody>
                    {pricing.measurementSummaryRows.map((row, index) => (
                      <tr key={`ms-summary-${index}`} className={row.isGrand ? 'summary-grand' : ''}>
                        <td>{row.isDiscount ? <><span>İndirim </span><span className="discount-mark">(-)</span></> : row.label}</td>
                        <td className="amount">{formatTl(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {pricing.formulaRows.length > 0 && (
            <div className="price-table-wrapper measurement-table">
              <table className="price-table price-summary-table">
                <tbody>
                  {pricing.formulaRows.map((row, index) => (
                    <tr key={`formula-${index}`}>
                      <td>{row[0]}</td>
                      <td className="amount">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="cta-card">
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} lg={16}>
                <h3>Operasyonel Güvenliğinizi Bugün Başlatın</h3>
                <p>
                  Bu teklifi onaylamak için QR kodu taratabilir veya dijital onay butonunu kullanabilirsiniz.
                  Onayınızın ardından 24 saat içinde saha planlamanız yapılacaktır.
                </p>
                <Button type="primary" size="large">Hemen Dijital Onayla</Button>
              </Col>
              <Col xs={24} lg={8}>
                <div className="qr-placeholder" aria-hidden="true">
                  <div className="qr-grid" />
                  <span>DİJİTAL ONAY QR</span>
                </div>
              </Col>
            </Row>
          </div>

          <Row gutter={[24, 24]} className="manual-sign-box">
            <Col xs={24} lg={16}>
              <div className="manual-sign-title">MANUEL ONAY VE İMZA</div>
              <div className="manual-sign-area">Kaşe / Islak İmza Alanı</div>
            </Col>
            <Col xs={24} lg={8}>
              <div className="manual-sign-contact">
                <div>info@kontrolx.com.tr</div>
                <div>{formatPhoneNumberDisplay('02124070405')}</div>
              </div>
            </Col>
          </Row>
        </section>

        <section ref={(node) => { sectionRefs.current.legal = node; }} data-section="legal" className="proposal-preview-section legal-section">
          <h2>Hizmet Sözleşmesi Genel Şartları</h2>

          <Row gutter={[24, 24]} className="legal-grid">
            {legalSections.map((section) => (
              <Col xs={24} key={section.id}>
                <article>
                  <h3>{section.title}</h3>
                  <div className="legal-pdf-body" dangerouslySetInnerHTML={{ __html: section.html }} />
                </article>
              </Col>
            ))}
          </Row>
        </section>
      </main>
    </div>
  );
};

export default ProposalPreview;
